import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClinic } from "@/contexts/ClinicContext";

export interface NotificationFiltersData {
  clinicId?: string;
  searchTerm?: string;
  status?: string;
  category?: string;
  startDate?: string;
  endDate?: string;
  pageSize?: number;
}

interface NotificationFiltersProps {
  onFilterChange: (filters: NotificationFiltersData) => void;
}

export function NotificationFilters({ onFilterChange }: NotificationFiltersProps) {
  const today = new Date().toISOString().split('T')[0];

  // ← Clínica vem do contexto global
  const { clinics, selectedClinicId } = useClinic();

  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [pageSize, setPageSize] = useState("50");

  const handleFilter = () => {
    onFilterChange({
      // clinicId injetado pela página via contexto
      searchTerm: searchTerm || undefined,
      status: status !== "all" ? status : undefined,
      category: category !== "all" ? category : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      pageSize: parseInt(pageSize),
    });
  };

  const handleClear = () => {
    setSearchTerm("");
    setStatus("all");
    setCategory("all");
    setStartDate(today);
    setEndDate(today);
    setPageSize("20");
    onFilterChange({
      // clinicId injetado pela página via contexto
      pageSize: 20,
      startDate: today,
      endDate: today,
    });
  };

  const selectedClinicName = clinics.find(c => c.id === selectedClinicId)?.name;

  return (
    <div className="space-y-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowFilters(!showFilters)}
        className="filter-toggle"
      >
        {showFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {showFilters ? "Esconder Filtros" : "Mostrar Filtros"}
      </Button>

      {showFilters && (
        <div className="filter-box">

          {/* Linha 1: Busca + Status + Categoria + Registros por Página */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div className="space-y-1">
              <Label htmlFor="searchTerm" className="filter-label">Busca Rápida</Label>
              <Input
                id="searchTerm"
                placeholder="Telefone ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="status" className="filter-label">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="status" className="filter-input">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="REVIEW">REVIEW</SelectItem>
                  <SelectItem value="PENDING">PENDING</SelectItem>
                  <SelectItem value="SENDING">SENDING</SelectItem>
                  <SelectItem value="SENT">SENT</SelectItem>
                  <SelectItem value="DELIVERED">DELIVERED</SelectItem>
                  <SelectItem value="READ">READ</SelectItem>
                  <SelectItem value="RESPONDED">RESPONDED</SelectItem>
                  <SelectItem value="COMPLETE">COMPLETE</SelectItem>
                  <SelectItem value="ERROR">ERROR</SelectItem>
                  <SelectItem value="ABORTED">ABORTED</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="category" className="filter-label">Categoria</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="category" className="filter-input">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="CONFIRMACAO">CONFIRMACAO</SelectItem>
                  <SelectItem value="CONFIRMACAO-D2">CONFIRMACAO-D2</SelectItem>
                  <SelectItem value="CONFIRMACAO-D1">CONFIRMACAO-D1</SelectItem>
                  <SelectItem value="CONFIRMACAO-D0">CONFIRMACAO-D0</SelectItem>
                  <SelectItem value="LEMBRETE-D0">LEMBRETE-D0</SelectItem>
                  <SelectItem value="CONVITE-AGENDAMENTO">CONVITE-AGENDAMENTO</SelectItem>
                  <SelectItem value="CONVITE-REMARCACAO">CONVITE-REMARCACAO</SelectItem>
                  <SelectItem value="NO-SHOW">NO-SHOW</SelectItem>
                  <SelectItem value="REACTIVATION">REACTIVATION</SelectItem>
                  <SelectItem value="RECALL-6M">RECALL-6M</SelectItem>
                  <SelectItem value="INFO">INFO</SelectItem>
                  <SelectItem value="ERROR">ERROR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="pageSize" className="filter-label">Registros por Página</Label>
              <Select value={pageSize} onValueChange={setPageSize}>
                <SelectTrigger id="pageSize" className="filter-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Linha 2: Datas + Botões */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div className="space-y-1">
              <Label htmlFor="startDate" className="filter-label">Data Início</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="filter-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="endDate" className="filter-label">Data Fim</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="filter-input"
              />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={handleFilter} size="sm" className="flex-1 gap-1.5 h-7 text-xs">
                <Search className="h-3 w-3" /> Filtrar
              </Button>
              <Button variant="outline" onClick={handleClear} size="sm" className="flex-1 gap-1.5 h-7 text-xs">
                <RotateCcw className="h-3 w-3" /> Limpar
              </Button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}